/**
 * La primitiva geométrica de la capa de iconos. Todo icono del set se dibuja acá adentro, y de
 * acá sale la única geometría que el repo reconoce como canónica.
 *
 * El grid es `viewBox 0 0 24 24` y el trazo por defecto es 2. No es arbitrario: a 16px de render,
 * 2 × (16/24) da **1.33px** efectivos, que es el peso que el repo ya tenía —`au/ui` dibujaba a
 * 1.5 sobre grid 16 renderizando a 14px, o sea 1.31px—. La normalización cambia el grid sin
 * cambiar lo que se ve.
 *
 * Dos formas, no una: `Icon` para los de trazo (la mayoría) e `IconSolid` para los que son
 * inherentemente rellenos —un cursor, una "T" de texto, un play—. Lo que NO varía es de dónde sale
 * el color: siempre `currentColor`. Un hex dentro de un icono es un icono que no responde al tema.
 *
 * Accesibilidad: por defecto el icono es decorativo (`aria-hidden`). Si lleva significado propio
 * —un botón que es solo el icono— se le pasa `title` y deja de ser invisible para lectores de
 * pantalla.
 */
import type { ComponentProps, ReactNode } from 'react'

/**
 * **Precedencia de dimensiones: `width`/`height` explícitos ganan sobre `size`.**
 *
 * `size` es el atajo para fijar los dos lados a la vez y es lo que se usa en el 95% de los casos.
 * Pasar `width` o `height` por separado es el override deliberado, y es el mecanismo con el que la
 * capa saca iconos fuera de la escala de sus wrappers: `ui/icons.tsx` los envuelve con `size={14}`
 * fijo, y los 15 call sites que necesitan 11, 12 o 13 px —`checkbox.tsx`, `combobox.tsx`,
 * `field.tsx`, `number-field.tsx`, `select`, `chip`, `menu`, `toast`— lo consiguen con
 * `width`/`height`. No es un accidente del spread: está declarado acá y aplicado explícitamente en
 * `Svg`.
 */
export interface IconProps extends Omit<ComponentProps<'svg'>, 'children'> {
  /**
   * Lado del icono en px. Fija `width` y `height` a la vez; el `viewBox` no cambia nunca.
   * Es el **default**: un `width`/`height` explícito lo pisa, lado por lado.
   */
  size?: number
  /** Override del lado horizontal. Gana sobre `size`. */
  width?: ComponentProps<'svg'>['width']
  /** Override del lado vertical. Gana sobre `size`. */
  height?: ComponentProps<'svg'>['height']
  /** Grosor del trazo sobre el grid 24. A partir de `size` 24 conviene bajarlo a 1.5. */
  strokeWidth?: number
  /** Si está presente, el icono deja de ser decorativo y se anuncia con este texto. */
  title?: string
}

interface SvgProps extends IconProps {
  children: ReactNode
}

function Svg({ size = 16, width, height, title, children, ...props }: SvgProps) {
  // La precedencia se aplica acá, no en el orden del spread: `width ?? size` lo dice donde se lee,
  // y además no deja que un `width={undefined}` explícito borre el lado en vez de caer al default.
  return (
    <svg
      width={width ?? size}
      height={height ?? size}
      viewBox="0 0 24 24"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  )
}

/** Icono de trazo — el caso normal. */
export function Icon({ strokeWidth = 2, children, ...props }: SvgProps) {
  return (
    <Svg
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </Svg>
  )
}

/** Icono de relleno — solo para formas que como contorno no se leen (play, cursor, texto). */
export function IconSolid({ children, ...props }: SvgProps) {
  return (
    <Svg fill="currentColor" {...props}>
      {children}
    </Svg>
  )
}

export type Direction = 'up' | 'right' | 'down' | 'left'

/**
 * Los direccionales son un dibujo y una rotación, no cuatro dibujos. Evita que la flecha que
 * apunta a la izquierda derive de la que apunta a la derecha, que es exactamente cómo el repo
 * terminó con cuatro chevrons distintos.
 */
const ROTATION: Record<Direction, number> = { down: 0, left: 90, up: 180, right: 270 }

export function Rotate({ facing, children }: { facing: Direction; children: ReactNode }) {
  const deg = ROTATION[facing]
  return deg === 0 ? <>{children}</> : <g transform={`rotate(${deg} 12 12)`}>{children}</g>
}
